<script setup>
import { ref, computed, watch } from 'vue'
import MapLayerPopup from '@/components/map/MapLayerPopup.vue'
import { formatTime } from '@/utils/datetime'
import { area } from '@turf/turf'
import { hasAreaGeometry } from '@/utils/geometry'
import { Point, LineString, Polygon, MultiPolygon } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { HOST, API } from '@/utils/http'

const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  isVisible: {
    type: Boolean,
    required: true
  },
  coordinates: {
    type: Array,
    default: null
  },
  feature: {
    type: Object,
    default: null
  },
  counter: {
    type: Number,
    default: 0
  },
  settings: {
    type: Object,
    required: true
  },
  code: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close'])

const isLoadingFeatureDetails = ref(false)
const featureDetails = ref(null)
const projectData = ref(null)
const isLoadingProjectData = ref(false)

const fetchFeatureDetails = async (featureCode) => {
  if (!featureCode) return

  isLoadingFeatureDetails.value = true
  featureDetails.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${featureCode}`
    })

    if (response.data) {
      featureDetails.value = response.data

      if (response.data.properties?.project_id) {
        await fetchProjectData(response.data.properties.project_id)
      }
    }
  } catch (error) {
    console.error('Error loading feature details:', error)
  } finally {
    isLoadingFeatureDetails.value = false
  }
}

const fetchProjectData = async (projectId) => {
  if (!projectId) return

  isLoadingProjectData.value = true
  projectData.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/project/${projectId}`
    })

    if (response.data) {
      projectData.value = response.data
    }
  } catch (error) {
    console.error('Error loading project data:', error)
  } finally {
    isLoadingProjectData.value = false
  }
}

const closePopup = () => {
  emit('close')
}

watch(() => [props.feature, props.counter], ([newFeature]) => {
  if (newFeature?.properties?.code) {
    fetchFeatureDetails(newFeature.properties.code)
  } else {
    featureDetails.value = null
    projectData.value = null
  }
}, { immediate: true })
</script>

<template>
  <MapLayerPopup
    v-if="isVisible && coordinates"
    :map="map"
    :coordinates="coordinates"
    :show="isVisible"
    :offset="10"
    :max-width="'300px'"
    :min-width="'300px'"
    @close="closePopup"
    :key="`feature-${feature?.id || code}-popup-${counter}`"
  >
    <div v-if="feature" class="p-2" style="min-width: 250px;">
      <div v-if="isLoadingFeatureDetails">
        Loading detailed information...
      </div>

      <div v-else-if="featureDetails">
        <div v-if="featureDetails.properties">
          <div class="flex items-center -mt-1 mb-1.5 text-xs text-muted-foreground">
            <span v-if="isLoadingProjectData">Loading project...</span>
            <span v-else-if="projectData">{{ projectData.project.project_name }}</span>
            <span v-else>{{ featureDetails.properties.project_id || 'Unknown Project' }}</span>
          </div>

          <div class="flex items-center mb-2">
            <div class="mr-2 w-8 h-8 rounded-full shadow-sm flex items-center justify-center bg-primary">
              <div v-if="featureDetails.properties?.s3_icon">
                <img class="object-contain w-5 h-5" :src="featureDetails.properties?.s3_icon" alt="">
              </div>
              <div v-else>
                <Point v-if="featureDetails.geometry.type === 'Point'" class="fill-white" />
                <LineString v-if="featureDetails.geometry.type === 'LineString'" class="fill-white" />
                <Polygon v-if="featureDetails.geometry.type === 'Polygon'" class="fill-white" />
                <Polygon v-if="featureDetails.geometry.type === 'GeometryCollection'" class="fill-white" />
                <MultiPolygon v-if="featureDetails.geometry.type === 'MultiPolygon'" class="fill-white" />
              </div>
            </div>
            <div>
              <div class="font-semibold text-sm">{{ featureDetails.properties.name || 'Unnamed Feature' }}</div>
              <div class="text-xs text-muted-foreground">{{ featureDetails.properties.member_display_name || 'Unknown User' }}</div>
            </div>
          </div>

          <div v-if="featureDetails.properties.shared" class="mb-2">
            <Badge variant="secondary" class="text-xs">Public</Badge>
          </div>

          <div v-if="featureDetails.properties.description" class="text-sm my-2 text-foreground">
            {{ featureDetails.properties.description }}
          </div>

          <ul class="text-xs text-muted-foreground mb-3 space-y-1">
            <li class="flex items-center">
              <span class="font-medium">{{ formatTime(featureDetails.properties.created_at, settings.tz, 'D MMMM YYYY HH:mm') }}</span>
            </li>
            <li v-if="featureDetails.properties.updated_at" class="flex items-center">
              <span class="text-muted-foreground mr-1">Updated:</span>
              <span class="font-medium">{{ formatTime(featureDetails.properties.updated_at, settings.tz, 'D MMMM YYYY HH:mm') }}</span>
            </li>
            <li v-if="hasAreaGeometry(featureDetails.geometry)" class="flex items-center">
              <span class="text-muted-foreground mr-1">Area:</span>
              <span class="font-medium">{{ (area(featureDetails.geometry) / 10000).toFixed(4) }} ha ({{ (area(featureDetails.geometry) / 1000000).toFixed(4) }} km²)</span>
            </li>
          </ul>
        </div>

        <div v-if="featureDetails.properties && featureDetails.properties.data">
          <div class="text-xs font-medium mb-2 text-muted-foreground">Project Data</div>
          <div class="flex flex-wrap gap-1">
            <template v-if="typeof featureDetails.properties.data === 'object'">
              <span
                v-for="(item, index) in featureDetails.properties.data"
                :key="index"
                class="px-1.5 py-0.5 bg-secondary rounded"
              >
                {{ item.key }}: {{ item.value }}
              </span>
            </template>
            <span v-else class="px-1.5 py-0.5 bg-secondary rounded">
              {{ featureDetails.properties.data }}
            </span>
          </div>
        </div>

        <!-- Images -->
        <div v-if="featureDetails.properties?.resources && featureDetails.properties.resources.length > 0" class="mb-3">
          <div class="text-xs font-medium mb-2 text-muted-foreground">Images</div>
          <div class="grid grid-cols-4 gap-1">
            <div v-for="(resource, idx) in featureDetails.properties.resources.filter(r => r.type === 'thumbnail')" :key="idx" class="relative">
              <img :src="resource.url" class="w-full h-16 object-cover rounded" alt="thumbnail" />
            </div>
          </div>
        </div>

        <div v-if="featureDetails.attributes && Object.keys(featureDetails.attributes).length > 0" class="bg-muted p-3 rounded-md">
          <div class="text-xs font-medium mb-1 text-muted-foreground">Details</div>
          <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <template v-for="(value, key) in featureDetails.attributes" :key="key">
              <div class="text-muted-foreground truncate">{{ key }}:</div>
              <div class="font-medium truncate">{{ value }}</div>
            </template>
          </div>
        </div>
      </div>

      <div v-else-if="feature.properties" class="bg-muted p-3 rounded-md">
        <div class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <template v-for="(value, key) in feature.properties" :key="key">
            <div class="text-muted-foreground truncate">{{ key }}:</div>
            <div class="font-medium truncate">{{ value }}</div>
          </template>
        </div>
      </div>

      <div v-else>
        No properties available for this feature
      </div>
    </div>
  </MapLayerPopup>
</template>
